import { Checkbox, Input, Label, cn } from '@design-system';

import { useRef } from 'react';

import type { AnswerOption } from '@shared';

interface QuestionBodyMultiSelectProps {
  options: AnswerOption[];
  value: string[];
  onChange: (value: string[]) => void;
  otherValue?: string;
  onOtherValueChange?: (value: string) => void;
  disabled?: boolean;
}

const OTHER_VALUE = 'other';
const NONE_VALUE = 'none';

export function QuestionBodyMultiSelect({
  options,
  value,
  onChange,
  otherValue,
  onOtherValueChange,
  disabled,
}: QuestionBodyMultiSelectProps) {
  const selected = new Set(value);
  const isNoneSelected = selected.has(NONE_VALUE);
  const otherInputRef = useRef<HTMLInputElement>(null);

  const toggle = (optionValue: string) => {
    const next = new Set(selected);

    if (optionValue === NONE_VALUE) {
      if (next.has(NONE_VALUE)) {
        next.delete(NONE_VALUE);
      } else {
        next.clear();
        next.add(NONE_VALUE);
      }
    } else {
      if (next.has(optionValue)) {
        next.delete(optionValue);
      } else {
        next.delete(NONE_VALUE);
        next.add(optionValue);
      }
    }
    onChange(Array.from(next));
  };

  return (
    <div className="space-y-2">
      {options.map(option => {
        const checked = selected.has(option.value);
        const isDisabledRow =
          disabled ||
          (isNoneSelected && option.value !== NONE_VALUE) ||
          (selected.size > 0 && !isNoneSelected && option.value === NONE_VALUE && !checked);
        const id = `multi-${option.value}`;
        const isEditableOther =
          option.value === OTHER_VALUE && onOtherValueChange != null && !isDisabledRow;

        if (isEditableOther && checked) {
          return (
            <div
              key={option.value}
              className={cn(
                'border-primary/30 bg-primary/10 flex w-full items-center gap-3 rounded-md border p-4 transition-colors',
              )}
              onClick={() => otherInputRef.current?.focus()}
              role="presentation"
            >
              <Checkbox
                id={id}
                checked
                disabled={isDisabledRow}
                onCheckedChange={() => toggle(option.value)}
                onClick={e => e.stopPropagation()}
              />
              <Input
                ref={otherInputRef}
                autoFocus
                placeholder="Please specify"
                value={otherValue ?? ''}
                onChange={e => onOtherValueChange?.(e.target.value)}
                onClick={e => e.stopPropagation()}
                disabled={isDisabledRow}
                className="h-auto border-0 bg-transparent p-0 text-sm font-medium text-gray-950 shadow-none focus-visible:ring-0 md:text-sm"
              />
            </div>
          );
        }

        return (
          <Label
            key={option.value}
            htmlFor={id}
            className={cn(
              'border-border bg-background flex w-full cursor-pointer items-center gap-3 rounded-md border p-4 transition-colors',
              'hover:border-primary/30',
              {
                'border-primary/30 bg-primary/10': checked,
                'cursor-not-allowed opacity-50': isDisabledRow,
              },
            )}
          >
            <Checkbox
              id={id}
              checked={checked}
              disabled={isDisabledRow}
              onCheckedChange={() => toggle(option.value)}
            />
            <span className="text-sm font-medium text-gray-950">{option.label}</span>
          </Label>
        );
      })}
    </div>
  );
}
